import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Select, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { CollapseBlock } from '../index.jsx';
import { useTranslationHandler } from '../../../hooks/useTranslationHandler.jsx';
import {
  useGetAllProvidersQuery,
  useGetProviderUsageMutation,
} from '../../../redux/services/translations.jsx';
import { useToastMessages } from '../../../hooks/useToastMessages.jsx';

/**
 * UI component providing graphical controls
 * for automatic translation of multilingual form fields.
 *
 * Allows administrator to:
 * - select translation provider
 * - trigger translation between configured languages
 * - access translation functionality within collapsible panel
 */
const TranslationProviderBlock = ({
                                    form,
                                    srcLang = 'sk',
                                    targetLang = 'en',
                                    fields = ['title', 'content'],
                                    closedByDefault = false,
                                  }) => {
  const { t } = useTranslation();
  const [provider, setProvider] = useState(null);
  const [usage, setUsage] = useState(null);
  const { handleTranslation, isLoading } =
    useTranslationHandler(form, fields);

  const { data: providersData, isLoading: providersLoading } =
    useGetAllProvidersQuery();

  const [getProviderUsage] = useGetProviderUsageMutation();

  const { Text } = Typography;
  const { successMessage } = useToastMessages();

/*  useEffect(() => {
    console.log(providersData);
  }, [providersData]);*/

  const handleProviderChange = (selectedProvider) => {
    setProvider(selectedProvider);

    getProviderUsage({ provider: selectedProvider })
      .unwrap()
      .then((res) => {
        setUsage(res?.usage ?? null);
      })
      .catch(() => {
        setUsage(null);
      });
  };

  useEffect(() => {
    const selectedProvider =
      providersData?.providers?.length === 1
        ? providersData.providers[0].value
        : null;

    if (!selectedProvider) return ;

    handleProviderChange(selectedProvider);
  }, [providersData]);

  /*useEffect(() => {
    console.log(usage)
  }, [usage]);*/
  const translationEnabled =
    import.meta.env.VITE_TRANSLATION_ENABLED === 'true';

  if (!translationEnabled || providersLoading || providersData === undefined || !providersData?.providers?.length) {
    return null;
  }
  return (
    <CollapseBlock
      collapsible
      collapse={false}
      title={t('Preklad')}
      closedByDefault={closedByDefault}
    >
      <div style={{ display: 'flex', marginBottom: 8 }}>
        <Space style={{ width: '100%' }} orientation="vertical">
          <Alert
            type="info"
            showIcon
            title={t('Automatický preklad všetkých textových polí vo formulári. Výsledok odporúčame skontrolovať a upraviť.')}
          />
          {(providersData.providers?.length ?? 0) > 1 ? (
            <>
              <Text>
                {t('Vyberte poskytovateľa prekladu (API), ktorý sa má použiť.')}
              </Text>
              <Select
                value={provider}
                onChange={handleProviderChange}
                style={{ width: 240 }}
                options={providersData.providers}
                loading={providersLoading}
                disabled={providersLoading}
              />
            </>
          ) : (
            <Text>
              {t('Poskytovateľ')}:{' '}
              <Text strong>
                {providersData.providers?.find((p) => p.value === provider)
                  ?.label ?? provider}
              </Text>
            </Text>
          )}

          {usage?.limit != null && (
            <Text type="secondary">
              {t('Využitie')}: {usage.usage?.toLocaleString()} /{' '}
              {usage.limit?.toLocaleString()} {t('znakov')}
            </Text>
          )}

          <Button
            type="primary"
            loading={isLoading}
            onClick={async () => {
              await handleTranslation(srcLang, targetLang, provider).then(() => {
                successMessage({ message: t('Preklad úspešný')});
              });
              getProviderUsage({ provider })
                .unwrap()
                .then((res) => setUsage(res?.usage ?? null))
                .catch(() => setUsage(null));
            }}
          >
            {t('Preložiť')}
          </Button>
        </Space>
      </div>
    </CollapseBlock>
  );
};

TranslationProviderBlock.propTypes = {
  form: PropTypes.object.isRequired,
  srcLang: PropTypes.string,
  targetLang: PropTypes.string,
  closedByDefault: PropTypes.bool,
};

export default TranslationProviderBlock;
